import React, { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {
  User, Mail, Lock, MapPin, Calendar, Globe, ChefHat, Coffee, Utensils,
  Clock, Wine, Flame, AlertCircle, Upload, Loader, Phone
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { signInWithGoogle } from '../lib/auth';
import { useNavigate, Link } from 'react-router-dom';
import LocationSearch from '../components/LocationSearch';
import { 
  cuisineOptions, 
  dietaryOptions, 
  priceRangeOptions, 
  languageOptions, 
  interestOptions 
} from '../data/filterOptions';

type Language = 'en' | 'de';

const Register = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null | JSX.Element>(null);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<'email' | 'phone'>('email');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const privacyLabels: Record<string, string> = {
    show_age: 'Alter anzeigen',
    show_location: 'Standort anzeigen',
    show_languages: 'Sprachen anzeigen',
    show_interests: 'Interessen anzeigen',
    show_dietary_preferences: 'Essgewohnheiten anzeigen',
    show_drinking_preference: 'Trinkverhalten anzeigen',
    show_smoking_preference: 'Rauchverhalten anzeigen',
  };

  const [formData, setFormData] = useState({
    fullName: '', 
    email: '', 
    password: '', 
    confirmPassword: '', 
    phone: '',
    dateOfBirth: '',
    location: '', 
    location_lat: null as number | null, 
    location_lng: null as number | null, 
    bio: '',
    favoriteCuisines: [] as string[], 
    dietaryPreferences: [] as string[], 
    priceRange: '', 
    preferredDiningTimes: [] as string[],
    languages: [] as string[], 
    interests: [] as string[], 
    diningStyle: '', 
    drinkingPreference: '', 
    smokingPreference: '',
    privacySettings: {
      profile_visibility: 'public', 
      show_age: true, 
      show_location: true, 
      show_interests: true,
      show_languages: true, 
      show_smoking_preference: true, 
      show_dietary_preferences: true, 
      show_drinking_preference: true
    }
  });

  const diningTimeOptions = [
    'Früher Morgen (06:00-08:00)', 'Frühstück (08:00-10:00)',
    'Brunch (10:00-12:00)', 'Mittagessen (12:00-14:00)',
    'Nachmittag (14:00-17:00)', 'Frühes Abendessen (17:00-19:00)',
    'Abendessen (19:00-21:00)', 'Später Abend (21:00+)'
  ];

  const diningStyleOptions = [
    { value: 'casual', label: 'Casual' },
    { value: 'fine_dining', label: 'Fine Dining' },
    { value: 'social', label: 'Sozial' },
    { value: 'adventurous', label: 'Erlebnisdinner' }
  ];

  const drinkingPreferenceOptions = [
    { value: 'non_drinker', label: 'Nicht-Trinker' },
    { value: 'social_drinker', label: 'Gesellschaftstrinker' },
    { value: 'wine_enthusiast', label: 'Wein Liebhaber' },
    { value: 'cocktail_lover', label: 'Cocktail Enthusiast' },
    { value: 'beer_enthusiast', label: 'Bier Liebhaber' }
  ];

  const smokingPreferenceOptions = [
    { value: 'non_smoker', label: 'Nichtraucher' },
    { value: 'social_smoker', label: 'Party Raucher' },
    { value: 'regular_smoker', label: 'Raucher' },
    { value: 'vaper', label: 'Vaper' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      location: location.address,
      location_lat: location.lat,
      location_lng: location.lng
    }));
  };

  const handleCheckboxChange = (name: string, value: string) => {
    setFormData(prev => {
      const currentValues = prev[name as keyof typeof prev] as string[];
      return {
        ...prev,
        [name]: currentValues.includes(value)
          ? currentValues.filter(item => item !== value)
          : [...currentValues, value]
      };
    });
  };

  const handleProfileVisibilityChange = (visibility: string) => {
    setFormData(prev => ({
      ...prev,
      privacySettings: { ...prev.privacySettings, profile_visibility: visibility }
    }));
  };

  const handlePrivacyChange = (key: string) => {
    setFormData(prev => ({
      ...prev,
      privacySettings: {
        ...prev.privacySettings,
        [key]: !prev.privacySettings[key as keyof typeof prev.privacySettings]
      }
    }));
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        setError('Profilbilder müssen unter 2MB sein');
        return;
      }
      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
    }
  };

  const handleGoogleSignUp = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const result = await signInWithGoogle();
      
      if (result.error) {
        setError(result.error);
      } else if (result.url) {
        // User will be redirected to Google's login page
        window.location.href = result.url;
      }
    } catch (error: any) {
      console.error("Unexpected Google login error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (step < 4) {
      setStep(step + 1);
      return;
    }

    // Password validation for both email and phone registration
    if (formData.password !== formData.confirmPassword) {
      setError("Passwörter sind nicht gleich");
      return;
    }
    
    if (formData.password.length < 6) {
      setError("Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }

    if (authMethod === 'email' && !captchaToken) {
      setError('Bitte bestätige, dass du kein Roboter bist.');
      return;
    }

    setLoading(true);
    try {
      console.log("Starting registration process");
      
      // Get the current origin for the redirect URL
      const redirectUrl = `${window.location.origin}/auth/callback`;

      // Prepare user metadata
      const userData = {
        full_name: formData.fullName,
        location: formData.location,
        location_lat: formData.location_lat,
        location_lng: formData.location_lng,
        bio: formData.bio,
        date_of_birth: formData.dateOfBirth,
        favorite_cuisines: formData.favoriteCuisines,
        dietary_preferences: formData.dietaryPreferences,
        price_range: formData.priceRange,
        preferred_dining_times: formData.preferredDiningTimes,
        languages: formData.languages,
        interests: formData.interests,
        dining_style: formData.diningStyle,
        drinking_preference: formData.drinkingPreference,
        smoking_preference: formData.smokingPreference,
        privacy_settings: formData.privacySettings
      };

      console.log("User metadata prepared:", userData);

      if (authMethod === 'email') {
        // Email registration
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: redirectUrl,
            data: userData,
            captchaToken: captchaToken || undefined
          }
        });

        // Error during signup?
        if (signUpError) {
          console.error("Sign up error:", signUpError);
          throw signUpError;
        }

        console.log("Auth data received:", authData);

        // Wait a moment to ensure all operations are complete
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Redirect to login page with confirmation notice
        navigate('/login', {
          state: { confirmationNotice: true }
        });
      } else {
        // Phone registration
        const { data, error } = await supabase.auth.signUp({
          phone: formData.phone,
          password: formData.password,
          options: {
            data: userData
          }
        });

        if (error) {
          console.error("Phone sign up error:", error);
          throw error;
        }

        console.log("Phone auth data received:", data);

        // Redirect to login page
        navigate('/login', {
          state: { phoneVerification: true }
        });
      }
    } catch (err) {
      console.error("Registration error:", err);
      const errorMessage = err instanceof Error ? err.message : 'Fehler bei der Registrierung';
      if (errorMessage.includes('already exists')) {
        setError(
          <div className="flex flex-col space-y-2">
            <span>{errorMessage}</span>
            <Link to="/login" className="text-orange-500 hover:text-orange-600">
              Hier klicken zum anmelden
            </Link>
          </div>
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Account erstellen</h2>
          <p className="mt-2 text-gray-600">Tritt unserer wachsenden Community bei</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-center items-center space-x-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>1</div>
            <div className={`h-1 w-16 ${step >= 2 ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>2</div>
            <div className={`h-1 w-16 ${step >= 3 ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 3 ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>3</div>
            <div className={`h-1 w-16 ${step >= 4 ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 4 ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>4</div>
          </div>
          <div className="flex justify-center mt-2">
            <span className="text-sm text-gray-500">
              {step === 1 ? 'Generelle Info' : 
               step === 2 ? 'Präferenzen beim Essen' : 
               step === 3 ? 'Zusatzinfo' :
               'Privacy Settings'}
            </span>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <div className="bg-white p-8 rounded-lg shadow-md">
          <form onSubmit={handleSubmit}>
             {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Authentication Method Selector */}
                <div className="flex border rounded-md overflow-hidden mb-4">
                  <button
                    type="button"
                    onClick={() => setAuthMethod('email')}
                    className={`flex-1 py-2 ${authMethod === 'email' ? 'bg-orange-500 text-white' : 'bg-gray-100'}`}
                  >
                    E-Mail
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMethod('phone')}
                    className={`flex-1 py-2 ${authMethod === 'phone' || authMethod === 'otp' ? 'bg-orange-500 text-white' : 'bg-gray-100'}`}
                  >
                    Telefon
                  </button>
                </div>

                {/* Profile Picture Upload */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative w-32 h-32">
                    {profilePicturePreview ? (
                      <img
                        src={profilePicturePreview}
                        alt="Profile preview"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition"
                    >
                      <Upload className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                  />
                  <span className="text-sm text-gray-500">
                   Profilbild hochladen (max 2MB)
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>Full Name</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border rounded-md"
                    required
                  />
                </div>

                {authMethod === 'email' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4" />
                          <span>E-Mail</span>
                        </div>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Lock className="h-4 w-4" />
                          <span>Password</span>
                        </div>
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Lock className="h-4 w-4" />
                          <span>Confirm Password</span>
                        </div>
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border rounded-md"
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4" />
                          <span>Phone Number</span>
                        </div>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border rounded-md"
                        placeholder="+491701234567"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Please enter your phone number in international format (e.g. +491701234567)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Lock className="h-4 w-4" />
                          <span>Password</span>
                        </div>
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Lock className="h-4 w-4" />
                          <span>Confirm Password</span>
                        </div>
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border rounded-md"
                        required
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>Date of Birth</span>
                    </div>
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>Location</span>
                    </div>
                  </label>
                  <LocationSearch
                    onLocationSelect={handleLocationSelect}
                    defaultValue={formData.location}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full p-2 border rounded-md"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                <div className="mt-4 text-center">
                  <p className="text-gray-600 mb-4">Google Registrierung wird bald auch möglich sein!</p>
                  {false && (
                     <button
                    type="button"
                    onClick={handleGoogleSignUp}
                    disabled={loading}
                    className="w-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 transition"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Register with Google
                  </button> 
                )}
                </div>
              </div>
            )}  

            {/* Step 2: Dining Preferences */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <ChefHat className="h-4 w-4" />
                      <span>Favorite Cuisines</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {cuisineOptions.map((cuisine) => (
                      <label key={cuisine} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.favoriteCuisines.includes(cuisine)}
                          onChange={() => handleCheckboxChange('favoriteCuisines', cuisine)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{cuisine}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Utensils className="h-4 w-4" />
                      <span>Dietary Preferences</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {dietaryOptions.map((option) => (
                      <label key={option} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.dietaryPreferences.includes(option)}
                          onChange={() => handleCheckboxChange('dietaryPreferences', option)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>Preferred Dining Times</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {diningTimeOptions.map((time) => (
                      <label key={time} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.preferredDiningTimes.includes(time)}
                          onChange={() => handleCheckboxChange('preferredDiningTimes', time)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{time}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Price Range</label>
                  <select
                    name="priceRange"
                    value={formData.priceRange}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border rounded-md"
                  >
                    <option value="">Select price range</option>
                    <option value="$">€ (Budget-friendly)</option>
                    <option value="$$">€€ (Moderate)</option>
                    <option value="$$$">€€€ (Upscale)</option>
                    <option value="$$$$">€€€€ (Luxury)</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 3: Additional Information */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4" />
                      <span>Languages</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {languageOptions.map((language) => (
                      <label key={language} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.languages.includes(language)}
                          onChange={() => handleCheckboxChange('languages', language)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{language}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Coffee className="h-4 w-4" />
                      <span>Interests</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {interestOptions.map((interest) => (
                      <label key={interest} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(interest)}
                          onChange={() => handleCheckboxChange('interests', interest)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Dining Style</label>
                  <select
                    name="diningStyle"
                    value={formData.diningStyle}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border rounded-md"
                  >
                    <option value="">Select dining style</option>
                    {diningStyleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Wine className="h-4 w-4" />
                      <span>Drinking Preference</span>
                    </div>
                  </label>
                  <select
                    name="drinkingPreference"
                    value={formData.drinkingPreference}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border rounded-md"
                  >
                    <option value="">Select drinking preference</option>
                    {drinkingPreferenceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Flame className="h-4 w-4" />
                      <span>Smoking Preference</span>
                    </div>
                  </label>
                  <select
                    name="smokingPreference"
                    value={formData.smokingPreference}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border rounded-md"
                  >
                    <option value="">Select smoking preference</option>
                    {smokingPreferenceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Step 4: Privacy Settings */}
            {step === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Privacy Settings</h3>
                
                {/* Profile Visibility */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Visibility
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.privacySettings.profile_visibility === 'public'}
                        onChange={() => handleProfileVisibilityChange('public')}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Public - Anyone can see your profile</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.privacySettings.profile_visibility === 'friends'}
                        onChange={() => handleProfileVisibilityChange('friends')}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Friends Only - Only accepted friends can see your profile</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.privacySettings.profile_visibility === 'private'}
                        onChange={() => handleProfileVisibilityChange('private')}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Private - Only you can see your profile</span>
                    </label>
                  </div>
                </div>
                
                {/* Information Visibility */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Information Visibility
                  </label>
                  <div className="space-y-2">
                    {Object.entries(formData.privacySettings)
                      .filter(([key]) => key !== 'profile_visibility')
                      .map(([key, value]) => (
                        <label key={key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={value as boolean}
                            onChange={() => handlePrivacyChange(key)}
                            className="rounded text-orange-500 focus:ring-orange-500"
                          />
                          <span>{privacyLabels[key] ?? key}</span>
                        </label>
                      ))}
                  </div>
                </div>

                {/* Captcha */}
                {authMethod === 'email' && (
                  <div className="mt-6">
                    <HCaptcha
                      sitekey="9ff831b8-4ae6-472a-9810-e68e0a1a1f96"
                      onVerify={(token) => setCaptchaToken(token)}
                      theme="light"
                      ref={captchaRef}
                    />
                  </div>
                )}

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-700 mb-2">Privacy Information</h3>
                  <p className="text-sm text-blue-600">
                    Your privacy is important to us. You can change these settings at any time from your profile settings page.
                    Information you choose to share will only be visible to other users according to your profile visibility setting.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-8 flex justify-between">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 transition"
                >
                  Back
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className={`ml-auto px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition flex items-center space-x-2 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>{step === 4 ? 'Create Account' : 'Next'}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;